import {Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {EquityInstallmentService, UpdateInstallmentObservableMode} from "../../equity-installment.service";
import {forkJoin, Subscription} from "rxjs";
import ProjectEquityInstallment from "../../../interfaces/project-equity-installment";
import {MatDialog} from "@angular/material/dialog";
import {
  AddEquityInstallmentMode,
  AddInstallmentFundingModalComponent
} from "../add-installment-funding-modal/add-installment-funding-modal.component";
import ProjectInstallmentStatus from "../../../interfaces/project-installment-status";
import {DatePipe} from "@angular/common";
import {MatBottomSheet} from "@angular/material/bottom-sheet";
import {
  EquityInstallmentInstallmentEntryComponent
} from "../equity-installment-installment-entry/equity-installment-installment-entry.component";

export enum InstallmentViewType {
  SYNDICATED = 'syndicated',
  CERTIFICATED = 'certificated'
}

@Component({
    selector: 'equity-installment-home',
    templateUrl: './equity-installment-home.component.html',
    styleUrls: ['./equity-installment-home.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EquityInstallmentHomeComponent implements OnInit{
  @Output() expandPanel: EventEmitter<any> = new EventEmitter<any>()
  public expandedInstallments = new Set<string>()
  @Input() panelExpanded: boolean = true;
  @Input() drawerBottom: boolean = false
  @Input() isSmallerScreen: boolean = false;
  @Input() serviceLoaded: boolean = false;
  @ViewChildren(EquityInstallmentInstallmentEntryComponent) entryComponents: QueryList<EquityInstallmentInstallmentEntryComponent>
  public installments:any[] = []
  public installmentStatuses: ProjectInstallmentStatus[] = []
  public projectId: string | null;
  public isLoading: boolean = false;
  public installmentSub: Subscription
  public displaySub: Subscription
  public statusSub: Subscription
  public loadingSub: Subscription
  public projectSub: Subscription
  public userSub: Subscription
  public permissionsSub: Subscription
  public permissions:any;
  public user: any;
  public projectDetails:any = {
    name: null,
    total_equity: 0,
    total_funded: 0,
    remaining: 0,
    total_adjusters: 0
  };
  public expandedIndex:number = 0;

  constructor(private activatedRoute:ActivatedRoute, public equityService: EquityInstallmentService, private dialog: MatDialog, private datePipe: DatePipe, private _bottomDrawer: MatBottomSheet) {
  }

  ngOnInit() {
    this.projectId = this.activatedRoute.snapshot.paramMap.get('id')
    if (this.projectId){
        this._getInstallmentsData(this.projectId)
    }
  }

  private _getInstallmentsData(projectId:string){
    this.equityService.fetchProjectInstallments(projectId, true)
    this.installmentSub = this.equityService.projectInstallments$.subscribe((result) => {
      this.installments = result;

      const normalize = (val?: string) => (val ?? '').trim().toLowerCase()

      this.installments = [...this.installments].sort((a:ProjectEquityInstallment, b:ProjectEquityInstallment) => {
        const aClosed = normalize(a.projectInstallmentStatus?.name) === 'paid'
        const bClosed = normalize(b.projectInstallmentStatus?.name) === 'paid'
        if(aClosed !== bClosed){
          return aClosed ? 1 : -1
        }

        if(a.installment_number < b.installment_number){
          return -1;
        }
        if(a.installment_number > b.installment_number){
          return 1;
        }
        let aDate = new Date(a.original_commitment_date ?? "");
        let bDate = new Date(b.original_commitment_date ?? "");
        if(aDate.getTime() < bDate.getTime()){
          return -1;
        }
        if(aDate.getTime() > bDate.getTime()){
          return 1;
        }
        return 0;
      })
      this.installments = this.installments.filter((installment:ProjectEquityInstallment) => this.equityService.viewType==InstallmentViewType.CERTIFICATED ? installment.certificated : !installment.certificated)
      //this.installments.forEach((installment:ProjectEquityInstallment) => this.expandedInstallments.add(installment.id.toString()))
      this._setTotals()
    })
    this.projectSub = this.equityService.project$.subscribe((result) => {
      this.projectDetails.name = result.name
    })

    this.statusSub = this.equityService.projectInstallmentStatuses$.subscribe((result) => {
      this.installmentStatuses = result
    })

    this.userSub = this.equityService.user$.subscribe((result) => this.user = result)
    this.permissionsSub = this.equityService.permissions$.subscribe((result) => {
      this.permissions = result;
    })

    this.loadingSub = this.equityService.loading$.subscribe((result) => {
      this.isLoading = result
    })

  }

  public saveAll(){
    forkJoin(this.entryComponents.map(e => e.save())).subscribe({
      next: () => {
        this.equityService.openSnackBar('All changes saved.', 'save-success');
        if (this.projectId) this._getInstallmentsData(this.projectId); // one refetch
      },
      error: () => this.equityService.openSnackBar('Some changes failed to save.', 'save-fail'),
    });
  }

  public get canEditAnything(): boolean {
    const p = this.permissions;
    return !!(p?.installmentPermissions?._edit || p?.fundingPermissions?._edit
      || p?.paymentPermissions?._edit || p?.conditionPermissions?._edit);
  }
  public togglePanel() {
    this.panelExpanded = !this.panelExpanded
    this.expandPanel.emit()
  }


  public trackById(index:number, item: ProjectEquityInstallment){
    return item.id
  }

  public onInstallmentOpen(id:string){
    this.expandedInstallments.add(id)
  }

  public onInstallmentClose(id:string){
    this.expandedInstallments.delete(id)
  }

  public isInstallmentOpen(id:string){
    return this.expandedInstallments.has(id)
  }
  // public openBottomDrawer(){
  //   this._bottomDrawer.open(EquityInstallmentDocumentsPanelComponent, {
  //     hasBackdrop: false,
  //     panelClass: 'panel-width',
  //     data: {
  //       title: "Equity"
  //     }
  //   })
  // }

  public onChangeInstallmentView(event:InstallmentViewType){
    this.equityService.viewType = event
    if(this.projectId){
      this._getInstallmentsData(this.projectId)
    }

  }

  private _setTotals(){
    let totalEquity = 0;
    let totalFunded = 0;
    let totalAdjusters = 0;
    let remaining = 0;
    for(let i = 0; i < this.installments.length; i++){
      totalEquity += this.installments[i].installment_amount
      totalFunded += this.installments[i].total_funded_amount
      totalAdjusters += this.installments[i].total_adjuster_amount
      remaining += this.installments[i].total_remaining_amount
    }
    this.projectDetails.total_equity = totalEquity
    this.projectDetails.total_funded = totalFunded
    this.projectDetails.total_adjusters = totalAdjusters
    this.projectDetails.remaining = remaining
  }

  public openAddInstallmentModal(){

    let dialogRef = this.dialog.open(AddInstallmentFundingModalComponent, {
      width: '800px',
      data: {
        modalType: AddEquityInstallmentMode.INSTALLMENT,
        statuses: this.installmentStatuses,
        projectId: this.projectId,
      }
    })

    let saveSub = dialogRef.componentInstance.save.subscribe((result:any) => {
      result.original_commitment_date = this.datePipe.transform(result.original_commitment_date, 'yyyy-MM-dd')
      result.certificated = this.equityService.viewType == InstallmentViewType.CERTIFICATED
      result.project_id = this.projectId
      this.equityService.createInstallment(result).subscribe((response:any) => {
        this.equityService.openSnackBar('Installment added.', 'save-success')
        // let rs = response as any;
        // let resultData = rs.data.createProjectInstallment
        // this.equityService.updateInstallments(UpdateInstallmentObservableMode.ADD,resultData)
        this.equityService.fetchProjectInstallments(result.project_id)
        dialogRef.close()
        saveSub.unsubscribe();
      })
    })
  }

  ngOnDestroy(){
    this.loadingSub.unsubscribe()
    this.projectSub.unsubscribe()
    this.installmentSub.unsubscribe()
    this.statusSub.unsubscribe()
    this.userSub.unsubscribe()
    this.permissionsSub.unsubscribe()
  }

  protected readonly InstallmentViewType = InstallmentViewType;
}
